import React from 'react';
import moment from 'moment-timezone';
import { CoachAvailabilityExceptionInput } from 'globalTypes';

import TimezoneHelper from 'lib/timezones';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import DatePickerInput from 'components/DatePickerInput';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TimePickerInput from 'components/TimePickerInput';
import ToastActions from 'actions/ToastActions';
import EventTracking from 'lib/EventTracking';
import { setCoachingAvailabilityExceptions } from 'coaching/actions/CoachingActions';
import CurrentUser from 'auth/CurrentUser';
import { AvailabilityQueryVariables } from 'coaching/containers/GetCoachAvailabilitiesAndTimezoneContainer';

const addHour = (startDate: Date) =>
  moment(startDate)
    .add(1, 'hour')
    .toDate();

type Props = {
  weekOffset: number;
  availabilityQuery: AvailabilityQueryVariables;
  onHide: () => void;
};

type State = {
  startDate: Date;
  endDate: Date;
  allDay: boolean;
};

export default class CoachingAvailabilityAddTimeOffView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const startDate = this.getDefaultDate(props.weekOffset);
    this.state = {
      startDate,
      endDate: addHour(startDate),
      allDay: false,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('coaching.add_time_off.view');
  }

  getDefaultDate = (weekOffset: number) => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + weekOffset * 7);
    defaultDate.setMinutes(0);
    return defaultDate;
  };

  getMinEndDate = () => addHour(this.state.startDate);

  // callback called whenever we update state.
  // ensures that the end date is always at least an hour after the start date.
  updateEndDate = () => {
    const minEndDate = this.getMinEndDate();
    if (this.state.endDate < minEndDate) {
      this.setState({ endDate: minEndDate }, () => {
        if (this.state.allDay) {
          this.setEndTimeForAllDay();
        }
      });
    }
  };

  setStartDate = (startDate?: Date) => {
    if (startDate) {
      this.setState({ startDate }, this.updateEndDate);
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setEndDate = (endDate?: Date) => {
    if (endDate) {
      this.setState({ endDate }, this.updateEndDate);
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setStartTime = (startTime?: Date) => {
    if (startTime) {
      const { startDate } = this.state;
      const newStartDateTime = startTime.getTime();
      startDate.setTime(newStartDateTime);

      this.setState({ startDate }, this.updateEndDate);
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setEndTime = (endTime?: Date) => {
    if (endTime) {
      const { endDate } = this.state;
      const newEndDateTime = endTime.getTime();
      endDate.setTime(newEndDateTime);

      this.setState({ endDate }, this.updateEndDate);
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  toggleAllDay = () => {
    this.setState({ allDay: !this.state.allDay }, () => {
      if (this.state.allDay) {
        this.setEndTimeForAllDay();
      }
    });
  };

  setEndTimeForAllDay = () => {
    const { startDate, endDate } = this.state;
    startDate.setHours(0);
    endDate.setHours(24);
    this.setState({ startDate, endDate });
  };

  handleSaveTimeOff = () => {
    const input = [
      {
        startDateTime: this.state.startDate.toISOString(),
        endDateTime: this.state.endDate.toISOString(),
      },
    ];
    setCoachingAvailabilityExceptions(
      input,
      TimezoneHelper.userTimezoneDefaultValue ?? '',
      CurrentUser.getUser().getUuid(),
      this.props.availabilityQuery
    ).then(this.props.onHide);
  };

  renderStartDatePicker(isAllDay: boolean) {
    return (
      <DatePickerInput
        full
        format="MMMM d"
        value={this.state.startDate}
        min={new Date()}
        onChange={(date?: Date) => {
          this.setStartDate(date);
          isAllDay
            ? EventTracking.trackKey('coaching.add_time_off.select_all_day_start_date.click')
            : EventTracking.trackKey('coaching.add_time_off.select_start_date.click');
        }}
      />
    );
  }
  renderEndDatePicker(isAllDay: boolean) {
    return (
      <DatePickerInput
        full
        format="MMMM d"
        value={this.state.endDate}
        min={this.getMinEndDate()}
        onChange={(date?: Date) => {
          this.setEndDate(date);
          isAllDay
            ? EventTracking.trackKey('coaching.add_time_off.select_all_day_end_date.click')
            : EventTracking.trackKey('coaching.add_time_off.select_end_date.click');
        }}
      />
    );
  }

  render() {
    const { allDay } = this.state;

    return (
      <Container size="xsmall">
        <MediaObject figure="right">
          <Heading serif>Add time off</Heading>
          <Link onClick={this.props.onHide}>
            <Icon name="x" color="chalkboard" />
          </Link>
        </MediaObject>
        <Text element="p" lineHeight="large">
          Add a one-off block of time for events like appointments or vacations when you’re
          unavailable during your normal availability.
        </Text>
        <Row>
          <Checkbox
            onChange={() => {
              this.toggleAllDay();
              EventTracking.trackKey('coaching.add_time_off.all_day.toggle');
            }}
            label="All day"
          />
        </Row>
        <Row size="small">
          <Label>Starts</Label>
          {allDay ? (
            this.renderStartDatePicker(allDay)
          ) : (
            <MediaObject figure="right" center gutter="small">
              {this.renderStartDatePicker(allDay)}
              <TimePickerInput
                step={60}
                value={this.state.startDate}
                onChange={(date?: Date) => {
                  this.setStartTime(date);
                  EventTracking.trackKey('coaching.add_time_off.select_start_time.click');
                }}
              />
            </MediaObject>
          )}
        </Row>
        <Row>
          <Label>Ends</Label>
          {allDay ? (
            this.renderEndDatePicker(allDay)
          ) : (
            <MediaObject figure="right" center gutter="small">
              {this.renderEndDatePicker(allDay)}
              <TimePickerInput
                step={60}
                value={this.state.endDate}
                min={this.getMinEndDate()}
                onChange={(date?: Date) => {
                  this.setEndTime(date);
                  EventTracking.trackKey('coaching.add_time_off.select_end_time.click');
                }}
              />
            </MediaObject>
          )}
        </Row>
        <Buttons full>
          <Button
            data-test="set-availability-button"
            onClick={this.handleSaveTimeOff}
            eventKey="coaching.add_time_off.add_time_off.click">
            Add time off
          </Button>
        </Buttons>
      </Container>
    );
  }
}
